import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  FormControlLabel,
  Switch,
  Collapse,
  IconButton,
} from '@mui/material';
import { Button } from '../button/button';
import { AUDIT } from '@flexo/reducers';
import styles from './table.module.scss';

interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

export function UsersAuditTable() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auditState = useSelector((state: any) => state.audit?.users);
  const { results, summary, loading, error, lastAuditAt } = auditState || {};

  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [includeAligned, setIncludeAligned] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    dispatch(AUDIT.AuditUsersAsync({ includeAligned: false }) as any);
  }, []);

  const handleRunAudit = () => {
    dispatch(AUDIT.AuditUsersAsync({ includeAligned }) as any);
  };

  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig?.field === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ field, direction });
  };

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const sortedResults = [...(results || [])].sort((a, b) => {
    if (!sortConfig) return 0;
    const aVal = a[sortConfig.field] || '';
    const bVal = b[sortConfig.field] || '';
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  const getIssueTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      email_mismatch: t('widgets.audit.issueTypes.emailMismatch'),
      name_mismatch: t('widgets.audit.issueTypes.nameMismatch'),
      missing_catalog_link: t('widgets.audit.issueTypes.missingCatalogLink'),
      orphaned_external_id: t('widgets.audit.issueTypes.orphanedExternalId'),
      missing_clerk_user: t('widgets.audit.issueTypes.missingClerkUser'),
      role_mismatch: t('widgets.audit.issueTypes.roleMismatch'),
    };
    return labels[type] || type;
  };

  const columns = [
    { field: 'expand', title: '', sortable: false },
    { field: 'email', title: t('widgets.audit.columns.email'), sortable: true },
    { field: 'firstName', title: t('widgets.audit.columns.name'), sortable: true },
    { field: 'isAligned', title: t('widgets.audit.columns.status'), sortable: true },
    { field: 'issueCount', title: t('widgets.audit.columns.issues'), sortable: false },
  ];

  return (
    <div className={styles.TableWrapper}>
      <div className={`${styles.TableHeader} flex flex-row flex-justify-between flex-align-center`}>
        <h2>{t('widgets.audit.title')}</h2>
        <div className="flex flex-row flex-align-center" style={{ gap: '16px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={includeAligned}
                onChange={(e) => setIncludeAligned(e.target.checked)}
              />
            }
            label={t('widgets.audit.includeAligned')}
          />
          <Button
            style={{ maxHeight: 'fit-content', maxWidth: 'fit-content', padding: '8px 24px' }}
            color="secondary"
            label={t('widgets.audit.runAudit')}
            onClick={handleRunAudit}
            disabled={loading}
          />
        </div>
      </div>

      {lastAuditAt && (
        <p style={{ color: 'var(--hive-greyscale-600)', fontSize: '14px', marginBottom: '12px' }}>
          {t('widgets.audit.lastRun')} {new Date(lastAuditAt).toLocaleString()}
        </p>
      )}

      {summary && (
        <div className="flex flex-row" style={{ gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <Chip
            label={`${t('widgets.audit.summary.total')}: ${summary.totalUsers}`}
            variant="outlined"
          />
          <Chip
            label={`${t('widgets.audit.summary.aligned')}: ${summary.alignedUsers}`}
            color="success"
            variant="outlined"
          />
          <Chip
            label={`${t('widgets.audit.summary.withIssues')}: ${summary.usersWithIssues}`}
            color={summary.usersWithIssues > 0 ? 'warning' : 'default'}
            variant="outlined"
          />
          {summary.issuesBySeverity?.error > 0 && (
            <Chip label={`Errors: ${summary.issuesBySeverity.error}`} color="error" />
          )}
          {summary.issuesBySeverity?.warning > 0 && (
            <Chip label={`Warnings: ${summary.issuesBySeverity.warning}`} color="warning" />
          )}
        </div>
      )}

      {error && (
        <div style={{ color: 'var(--hive-negative-500)', padding: '8px 0' }}>
          {error}
        </div>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.field} width={col.field === 'expand' ? 50 : undefined}>
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortConfig?.field === col.field}
                      direction={sortConfig?.field === col.field ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort(col.field)}
                    >
                      {col.title}
                    </TableSortLabel>
                  ) : (
                    col.title
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  {t('common.loading', { defaultValue: 'Running audit...' })}
                </TableCell>
              </TableRow>
            ) : !results || results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  {t('widgets.audit.noResults')}
                </TableCell>
              </TableRow>
            ) : (
              sortedResults.map((user: any) => {
                const rowId = user.clerkUserId || user.catalogUserId || user.email;
                const isExpanded = expandedRows.has(rowId);
                return (
                  <>
                    <TableRow key={rowId} hover>
                      <TableCell>
                        {user.issues?.length > 0 && (
                          <IconButton size="small" onClick={() => toggleRow(rowId)}>
                            {isExpanded ? '▼' : '▶'}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{[user.firstName, user.lastName].filter(Boolean).join(' ') || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.isAligned ? t('widgets.audit.aligned') : t('widgets.audit.issues')}
                          color={user.isAligned ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {user.issues?.length || 0}
                      </TableCell>
                    </TableRow>
                    {user.issues?.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={columns.length} sx={{ py: 0 }}>
                          <Collapse in={isExpanded}>
                            <div style={{ padding: '16px', backgroundColor: 'var(--hive-greyscale-100)' }}>
                              <strong style={{ fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                {t('widgets.audit.issueDetails')}
                              </strong>
                              {user.issues.map((issue: any, idx: number) => (
                                <div key={idx} style={{ marginBottom: '6px' }}>
                                  <Chip
                                    label={getIssueTypeLabel(issue.issueType)}
                                    color={getSeverityColor(issue.severity) as any}
                                    size="small"
                                    sx={{ mr: 1 }}
                                  />
                                  <span style={{ fontSize: '14px' }}>
                                    {issue.description}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default UsersAuditTable;
